import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import {API} from 'aws-amplify';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Unstable_Grid2';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import SelectStudentDialog from './SelectStudentDialog';
import SelectExerciseDialog from './SelectExerciseDialog';
import SelectLessonDialog from './SelectLessonDialog';
import ConfirmStudentDialog from './ConfirmStudentDialog';
import AssignmentTypeMenu from './AssignmentTypeMenu';
import {BluePillButton} from '../../common/components/BluePillButton';

import baseStyles from '../../css/Base.module.css';
import {LESSON, APP_TITLES, ENDPOINT} from '../../constants';

export default function CreateSession() {
  const history = useHistory();

  const [anchorEl, setAnchorEl] = useState(null);

  const [isSaving, toggleIsSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [isSelectStudentDialogOpen, setIsSelectStudentDialogOpen] = useState(false);
  const [isSelectLessonDialogOpen, setIsSelectLessonDialogOpen] = useState(false);
  const [isSelectExerciseDialogOpen, setIsSelectExerciseDialogOpen] = useState(false);
  const [isConfirmStudentDialogOpen, setIsConfirmStudentDialogOpen] = useState(false);
  const [isAssignmentTypeMenuOpen, setIsAssignmentTypeMenuOpen] = useState(false);
  const [assignmentType, setAssignmentType] = useState(null);
  const [studentSelected, setStudentSelected] = useState(null);

  function handleClickCancel() {
    history.push('/builder');
  }

  function handleClickAddAssignment(event) {
    setAnchorEl(event.currentTarget);
    setIsAssignmentTypeMenuOpen(true);
  }

  function handleClickAssignmentType(type) {
    setIsAssignmentTypeMenuOpen(false);
    setAssignmentType(type);
    if (type === LESSON) setIsSelectLessonDialogOpen(true);
    else setIsSelectExerciseDialogOpen(true);
  }

  function isSaveButtonEnabled() {
    return students.length > 0 && assignments.length > 0 && !isSaving;
  }

  function handleStudentSelected(student) {
    const studentExists = students.find((s) => s.id === student.id);

    if (!studentExists) {
      setStudentSelected(student);
      setIsConfirmStudentDialogOpen(true);
    }
  }

  function handleStudentCreated(student) {
    setStudents([...students, student]);
  }

  function handleConfirmStudent(student) {
    if (student) {
      setStudents([...students, student]);
      setIsConfirmStudentDialogOpen(false);
    }
  }

  function handleAssignmentSelected(selectedAssignment) {
    // Add type to lesson
    if (assignmentType === LESSON) selectedAssignment.type = LESSON;

    const assignmentExists = assignments.find(
      (assignment) => assignment._id === selectedAssignment._id && assignment.type === selectedAssignment.type,
    );

    if (!assignmentExists) {
      setAssignments([...assignments, selectedAssignment]);
    }
  }

  function handleClickRemoveAssignment(selectedAssignment) {
    setAssignments(
      assignments.filter(
        (assignment) => !(assignment._id === selectedAssignment._id && assignment.type === selectedAssignment.type),
      ),
    );
  }

  async function handleSave() {
    toggleIsSaving(true);

    const createSessionPayload = {
      body: {
        assignments: assignments.map((assignment) => ({
          type: assignment.type,
          id: assignment._id,
        })),
        students: students.map((student) => student.id),
      },
    };
    const {sessionID} = await API.post(ENDPOINT, '/assignments', createSessionPayload);

    toggleIsSaving(false);
    history.push(`/builder/session/${sessionID}`);
  }

  function formatAssignmentType(type) {
    if (type === LESSON) return LESSON;
    else return `${APP_TITLES[type].toUpperCase()} EXERCISE`;
  }

  function renderEmpty(onClickHandler, selectText) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px dashed black',
          borderRadius: 4,
          borderColor: 'rgba(0, 0, 0, 0.12)',
        }}
        className="m-10"
      >
        <Typography variant="subtitle1" className="m-t-20">
          Nothing has been selected yet.
        </Typography>
        <Typography variant="subtitle2" className="m-t-10 m-b-20">
          <Link component="button" onClick={onClickHandler}>
            Select
          </Link>{' '}
          {selectText}
        </Typography>
      </div>
    );
  }

  function renderStudentsBody() {
    return students.length > 0 ? (
      <List>
        {students.map((student) => (
          <React.Fragment key={student.id}>
            <ListItem
              secondaryAction={
                <Button onClick={() => setStudents(students.filter((s) => s.id !== student.id))}>REMOVE</Button>
              }
            >
              <ListItemText primary={`${student.lastName}, ${student.firstName}`} />
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    ) : (
      renderEmpty(() => setIsSelectStudentDialogOpen(true), 'students')
    );
  }

  function renderAssignmentsBody() {
    return assignments.length > 0 ? (
      <List>
        {assignments.map((assignment) => (
          <React.Fragment key={`${assignment.type}-${assignment._id}`}>
            <ListItem secondaryAction={<Button onClick={() => handleClickRemoveAssignment(assignment)}>REMOVE</Button>}>
              <ListItemText primary={assignment.name} secondary={formatAssignmentType(assignment.type)} />
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>
    ) : (
      renderEmpty(handleClickAddAssignment, 'lessons / exercises')
    );
  }

  return (
    <>
      <SelectStudentDialog
        isOpen={isSelectStudentDialogOpen}
        handleClose={() => setIsSelectStudentDialogOpen(false)}
        onSelectStudent={handleStudentSelected}
        onStudentCreate={handleStudentCreated}
        excludeStudents={students}
      />
      <AssignmentTypeMenu
        anchorEl={anchorEl}
        isOpen={isAssignmentTypeMenuOpen}
        setIsOpen={() => setIsAssignmentTypeMenuOpen(false)}
        handleClickType={handleClickAssignmentType}
      />
      <SelectExerciseDialog
        isOpen={isSelectExerciseDialogOpen}
        handleClose={() => setIsSelectExerciseDialogOpen(false)}
        exerciseType={assignmentType}
        onSelectExercise={handleAssignmentSelected}
      />
      <SelectLessonDialog
        isOpen={isSelectLessonDialogOpen}
        handleClose={() => setIsSelectLessonDialogOpen(false)}
        onSelectLesson={handleAssignmentSelected}
      />
      <ConfirmStudentDialog
        student={studentSelected}
        isOpen={isConfirmStudentDialogOpen}
        handleClose={() => setIsConfirmStudentDialogOpen(false)}
        handleConfirmStudent={handleConfirmStudent}
      />
      <PrimaryAppBar />
      <SecondaryAppBar title="Make Assignments" />
      <div className="p-20">
        <Grid container spacing={2}>
          <Grid xs={6}>
            <Paper sx={{padding: 2}}>
              <List>
                <ListItem
                  secondaryAction={
                    <Button
                      variant="outlined"
                      sx={{borderRadius: 18}}
                      onClick={() => setIsSelectStudentDialogOpen(true)}
                    >
                      SELECT
                    </Button>
                  }
                >
                  <ListItemText primary="STUDENTS" primaryTypographyProps={{variant: 'h5', className: 'fnt-bold'}} />
                </ListItem>
              </List>
              <Divider />
              {renderStudentsBody()}
            </Paper>
          </Grid>
          <Grid xs={6}>
            <Paper sx={{padding: 2}}>
              <List>
                <ListItem
                  secondaryAction={
                    <Button variant="outlined" sx={{borderRadius: 18}} onClick={handleClickAddAssignment}>
                      SELECT
                    </Button>
                  }
                >
                  <ListItemText primary="ASSIGNMENTS" primaryTypographyProps={{variant: 'h5', className: 'fnt-bold'}} />
                </ListItem>
              </List>
              <Divider />
              {renderAssignmentsBody()}
            </Paper>
          </Grid>
        </Grid>
      </div>
      <AppBar position="fixed" className="bg-gray-100" sx={{top: 'auto', bottom: 0}}>
        <Toolbar>
          <Box sx={{flexGrow: 1}}>
            <Grid container>
              <Grid xs display="flex" justifyContent="center" alignItems="center">
                <Button className={baseStyles['btn-pill']} onClick={handleClickCancel}>
                  Cancel
                </Button>
                <BluePillButton variant="outlined" disabled={!isSaveButtonEnabled()} onClick={handleSave}>
                  {isSaving ? <CircularProgress size={25} /> : 'Create Assignments'}
                </BluePillButton>
              </Grid>
            </Grid>
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar /> {/* Makes AppBar at the bottom of the page */}
    </>
  );
}
